import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { accentOf, Medallion } from '../components/achievements/Medallion';
import { useQualificationPlayer } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { ErrorView, Loader } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { QualificationChart } from '../components/qualification/QualificationChart';
import { VsIcon } from '../components/qualification/icons';
import { backButton } from '../telegram';
import { shortDate } from '../format';
import { Head } from './MyQualificationScreen';
import { SnowIcon } from './PassScreen';

const STATE_LABEL: Record<string, string> = {
  calibrating: 'в калибровке',
  ranked: 'в зачёте',
  warning: 'пропуски',
  decaying: 'затухание',
  at_floor: 'на старте',
};

/** Профиль игрока отбора (вариант Б): позиция + «Ты против него» + история. */
export function QualificationPlayerScreen() {
  const { id } = useParams();
  const pid = Number(id);
  const navigate = useNavigate();
  const { player } = useAuth();
  const q = useQualificationPlayer(pid);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (player && player.id === pid) return <Navigate to="/qualification/me" replace />;
  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data) return <ErrorView message={(q.error as Error)?.message} />;
  const data = q.data;
  const p = data.player;
  const cfg = data.qualification;
  if (!p || !cfg) {
    return (
      <div className="q-screen">
        <Head title="Отбор · игрок" onBack={() => navigate(-1)} />
        <div className="q-empty">Игрок ещё не играл в этом отборе.</div>
      </div>
    );
  }
  const h2h = data.headToHead ?? null;
  const inZone = p.rank !== null && p.rank <= cfg.tickets;

  return (
    <div className="q-screen">
      <Head title="Отбор · игрок" onBack={() => navigate(-1)} />

      <div className="q-player-head">
        <Avatar name={p.name} src={p.avatarUrl} size={56} />
        <div className="q-player-name">{p.name}</div>
        <div className="q-player-status">
          {inZone
            ? 'в зоне отбора'
            : p.rank !== null && p.rank <= cfg.tickets + 2
              ? 'в шаге от отбора'
              : STATE_LABEL[p.state] ?? p.state}
        </div>
      </div>

      <div className="q-pos-hero">
        <div className="q-pos-place">
          <div className="q-lbl">МЕСТО</div>
          <div className="v mono">
            {p.rank !== null ? p.rank : p.rankIfQualified !== null ? `~${p.rankIfQualified}` : '—'}
            {p.rankChangeWeek !== null && p.rankChangeWeek !== 0 && (
              <span className={`q-shift ${p.rankChangeWeek > 0 ? 'up' : 'dn'}`}>
                {p.rankChangeWeek > 0 ? `▲${p.rankChangeWeek}` : `▼${-p.rankChangeWeek}`}
              </span>
            )}
          </div>
        </div>
        <div className="q-pos-rating">
          <div className="q-lbl">РЕЙТИНГ</div>
          <div className="v mono">{p.rating}</div>
          <div className="sub mono">{p.weekDelta >= 0 ? `+${p.weekDelta}` : p.weekDelta} за неделю</div>
        </div>
      </div>

      {data.chart && data.chart.length >= 2 && (
        <QualificationChart
          points={data.chart}
          startRating={cfg.startRating}
          cutoff={p.cutoffRating}
          tickets={cfg.tickets}
        />
      )}

      {h2h && h2h.commonGames > 0 && (
        <div className="q-vs">
          <div className="q-vs-head">
            <VsIcon size={13} /> ТЫ ПРОТИВ НЕГО
          </div>
          <div className="q-vs-score mono">
            {h2h.myWins} : {h2h.theirWins}
          </div>
          <div className="q-vs-sub">
            кто выше в {h2h.commonGames} общих {pluralGames(h2h.commonGames)}
          </div>
          <div className="q-vs-tiles">
            <div className="q-vs-tile">
              <div className="v mono">
                {h2h.myRating !== null
                  ? `${h2h.myRating > p.rating ? '+' : ''}${h2h.myRating - p.rating}`
                  : '—'}
              </div>
              <div className="l">разница в рейтинге</div>
            </div>
            <div className="q-vs-tile">
              <div className="v mono">
                {h2h.myRank !== null && p.rank !== null
                  ? `${p.rank - h2h.myRank > 0 ? '+' : ''}${p.rank - h2h.myRank}`
                  : '—'}
              </div>
              <div className="l">разница в местах</div>
            </div>
          </div>
        </div>
      )}

      {data.selectionAchievements && data.selectionAchievements.earned > 0 && (
        <div className="q-sel-ach">
          <div className="q-vs-head">ДОСТИЖЕНИЯ ОТБОРА</div>
          <div className="q-sel-ach-row">
            {data.selectionAchievements.recent.map((a) => (
              <Medallion
                key={a.achievementId}
                icon={a.icon}
                level={a.level}
                maxLevel={a.maxLevel}
                size={48}
                ring={false}
                variant={accentOf(a.code, a.level, a.maxLevel)}
              />
            ))}
            <Link
              className="q-sel-ach-more mono"
              to={
                data.selectionAchievements.categoryId !== null
                  ? `/players/${pid}/achievements/categories/${data.selectionAchievements.categoryId}`
                  : `/players/${pid}/achievements`
              }
            >
              {data.selectionAchievements.earned}/{data.selectionAchievements.total} →
            </Link>
          </div>
        </div>
      )}

      <div className="q-stats q-stats--four">
        <div className="q-stat">
          <div className="v mono">{p.gamesPlayed}</div>
          <div className="l">сыграно</div>
        </div>
        <div className="q-stat">
          <div className="v mono">{p.bestPlace !== null ? `#${p.bestPlace}` : '—'}</div>
          <div className="l">лучшее место</div>
        </div>
        <div className="q-stat">
          <div className="v mono">{p.playersBeatenTotal}</div>
          <div className="l">обыграно</div>
        </div>
        <div className="q-stat">
          <div className="v mono">
            {p.sinceStart >= 0 ? `+${p.sinceStart}` : p.sinceStart}
          </div>
          <div className="l">с начала отбора</div>
        </div>
      </div>

      {data.history && data.history.length > 0 && (
        <>
          <div className="q-overline">ИСТОРИЯ ТУРНИРОВ</div>
          <div className="q-history">
            {data.history.map((h) => (
              <div key={`${h.gameId}-${h.kind}`} className="q-hist-row q-hist-row--static">
                <span className="dt mono">{shortDate(h.date)}</span>
                {h.kind === 'miss' ? (
                  <span className={h.frozen ? 'what q-frozen' : 'what q-neg'}>
                    {h.frozen ? (
                      <>
                        Пропуск · <SnowIcon size={10} /> заморожен
                      </>
                    ) : (
                      'Пропуск'
                    )}
                  </span>
                ) : (
                  <span className="what">
                    {h.place !== null ? `${h.place} место` : 'сыграно'}
                    {h.fieldSize !== null && ` из ${h.fieldSize}`}
                  </span>
                )}
                <span className={`d mono ${h.kind === 'miss' ? 'q-neg' : h.delta > 0 ? 'up' : ''}`}>
                  {h.delta >= 0 ? `+${h.delta}` : h.delta}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function pluralGames(n: number): string {
  const d = n % 10;
  const h = n % 100;
  if (h > 10 && h < 20) return 'турнирах';
  if (d === 1) return 'турнире';
  return 'турнирах';
}
